import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { LuUsers } from 'react-icons/lu';
import type { ExploreGroupListItem } from '@/client';
import { useGroups } from '@/contexts/GroupsContext';
import { groupIllustration } from '@/screens/home-search/group-illustrations';
import { SwitcherMenu, type SwitcherItem, type SwitcherVariant } from './SwitcherMenu';

interface GroupSwitcherProps {
    /** The group to name. */
    group?: ExploreGroupListItem | null;
    variant?: SwitcherVariant;
    /** Reads before the name in the hero — "Here's where the group is with". */
    prefix?: React.ReactNode;
}

/**
 * Which search group you are looking at.
 *
 * The Explore twin of `HomeSwitcher`, down to the menu shell they share. The
 * second line is the pair of numbers the design puts under each group — how
 * many of you there are, and how many homes you have saved between you.
 */
export function GroupSwitcher({ group, variant = 'hero', prefix }: GroupSwitcherProps) {
    const intl = useIntl();
    const navigate = useNavigate();
    const { groups } = useGroups();

    if (!group) return null;

    // Two messages rather than one: a single string with two plural rules in it
    // is more than most translators' tooling will take, and the lint rule says
    // so. The separator matches the home switcher's "place · N members".
    const items: SwitcherItem[] = groups.map((entry) => {
        const members = intl.formatMessage(
            {
                id: 'd40a7f18-5b92-4c36-8e07-1f6b3a9c5d82',
                defaultMessage: '{count, plural, one {# member} other {# members}}',
            },
            { count: entry.members?.length ?? 0 },
        );
        const saved = intl.formatMessage(
            {
                id: '5e1b7c04-9a36-4d82-b0f7-3c6e8a2d1495',
                defaultMessage: '{count, plural, one {# saved home} other {# saved homes}}',
            },
            { count: entry.saved_count ?? 0 },
        );

        return {
            id: entry.id,
            label: entry.name,
            meta: `${members} · ${saved}`,
            artwork: groupIllustration(entry),
        };
    });

    return (
        <SwitcherMenu
            variant={variant}
            prefix={prefix}
            icon={LuUsers}
            currentId={group.id}
            currentLabel={group.name}
            items={items}
            onSelect={(id) => navigate(`/groups/${id}`)}
            addLabel={intl.formatMessage({
                id: '9c04e7b3-1f58-4a26-8d70-5b3c9e1a6f42',
                defaultMessage: 'Start a new group',
            })}
            onAdd={() => navigate('/onboarding?flow=group')}
        />
    );
}
